import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';

import {
  DEFAULT_TIME_FORMAT,
  formatTime,
  formatTimeCompact,
  type FormatTimeOptions,
  hourCycleFor,
  isHour12,
  type TimeFormat,
} from './timeFormat';

export interface OrgTimeFormat {
  /** The org's configured format ('12h' | '24h'). */
  timeFormat: TimeFormat;
  /** `true` when the org uses the 12-hour clock. */
  hour12: boolean;
  /** For `lib/base` picker props (which must stay org-context-free). */
  hourCycle: 12 | 24;
  /**
   * `lib/time.formatTime` pre-bound to the org format. Prefer this over
   * threading `timeFormat` through props/params — components should read the
   * org default from context (via this hook), not from their parents.
   */
  formatTime: (value: string | Date, options?: FormatTimeOptions) => string;
  /** `lib/time.formatTimeCompact` pre-bound to the org format. */
  formatTimeCompact: (value: string | Date) => string;
}

/**
 * The effective time format for the current user in the current org, read
 * from OrgContext (client-cached, updated reactively on save), plus the
 * shared formatters pre-bound to it.
 *
 * Resolution: the member's personal preference
 * (`currentUserMembership.timeFormat`, set in Personal Info) wins; blank falls
 * back to the org-wide `defaultTimeFormat`; absent orgs fall back to the
 * Django default so rendering never flickers.
 *
 * The returned object and its functions are referentially stable for a given
 * format value, so they are safe in dependency arrays and props.
 */
export function useOrgTimeFormat(): OrgTimeFormat {
  const { currentOrg } = useOrg();
  const membershipFormat = currentOrg?.currentUserMembership?.timeFormat as
    | TimeFormat
    | ''
    | undefined;
  const timeFormat =
    membershipFormat ||
    (currentOrg?.defaultTimeFormat as TimeFormat | undefined) ||
    DEFAULT_TIME_FORMAT;
  return useMemo(
    () => ({
      timeFormat,
      hour12: isHour12(timeFormat),
      hourCycle: hourCycleFor(timeFormat),
      formatTime: (value: string | Date, options?: FormatTimeOptions) =>
        formatTime(value, timeFormat, options),
      formatTimeCompact: (value: string | Date) =>
        formatTimeCompact(value, timeFormat),
    }),
    [timeFormat]
  );
}
